import { AllActions } from "./rootReducer";

import { setCookie } from "common/cookies";
import getConfig from "next/config";
import { v4 as uuidv4 } from "uuid";
import { LOAD_COOKIES } from "redux/action-creators/cookies";
import { Dispensary } from "custom-types/Dispensary";
import { MenuItemVariant } from "custom-types/MenuItem";
import { menuTypeErrorMessage } from "common/menuTypes";

const { publicRuntimeConfig } = getConfig();

export enum ReservationStatus {
  None = "none",
  CartMismatch = "cartMismatch",
  ShowCheckoutModal = "showCheckoutModal",
  Unavailable = "unavailable",
  MenuTypeMismatch = "menuTypeMismatch",
}

type ReservationState = {
  cartId?: string;
  cart?: {
    cartItems: any[];
    dispensaryId: number;
    dispensary: Dispensary;
    loaded: boolean;
    quantityTotal: number;
    priceTotal: number;
  };
  pendingMenuItem?: {
    dispensary: Dispensary;
    menuItemId: number;
    quantity: number;
    variant: MenuItemVariant;
  };
  status: ReservationStatus;
};

const initialState: ReservationState = {
  status: ReservationStatus.None,
};

export const RESERVATION_LOAD_CART = "reservation/loadCart";
export const RESERVATION_ADD_TO_CART = "reservation/add";
export const RESERVATION_ADD_TO_CART_ERROR = "reservation/add/error";
export const RESERVATION_CREATE_CART_ID = "reservation/createCartId";
export const RESERVATION_CLOSE_MODAL = "reservation/closeModal";

export type LoadCartAction = {
  type: typeof RESERVATION_LOAD_CART;
  cart: {
    cartItems: Array<{
      quantity: number;
      packagePrice: number;
    }>;
    dispensaryId: number;
    dispensaryData: Dispensary;
  };
};
type CreateCartIdIfNotExistsAction = {
  type: typeof RESERVATION_CREATE_CART_ID;
  countryCode: string;
};
type AddToCartAction = {
  type: typeof RESERVATION_ADD_TO_CART;
  dispensary: Dispensary;
  quantity: number;
  variant: number;
};
type AddToCartErrorAction = {
  type: typeof RESERVATION_ADD_TO_CART_ERROR;
  error: string;
  pendingMenuItem?: {
    dispensary: Dispensary;
    menuItemId: number;
    quantity: number;
    variant: MenuItemVariant;
  };
};
export type CloseModalAction = {
  type: typeof RESERVATION_CLOSE_MODAL;
};

export type ReservationActions =
  | LoadCartAction
  | AddToCartAction
  | AddToCartErrorAction
  | CreateCartIdIfNotExistsAction
  | CloseModalAction;

const getAddToCartStatus = (action: AddToCartErrorAction) => {
  if (action.error) {
    if (action.error.includes("cart_mismatch_error")) {
      return ReservationStatus.CartMismatch;
    }

    if (
      action.error.includes("is not available") ||
      action.error.includes("no menu item")
    ) {
      return ReservationStatus.Unavailable;
    }

    if (action.error.includes(menuTypeErrorMessage)) {
      return ReservationStatus.MenuTypeMismatch;
    }
  }

  return ReservationStatus.None;
};

const createCartId = (countryCode: string) => {
  const leaflyId = uuidv4();
  setCookie(
    "X-Leafly-Id",
    leaflyId!,
    countryCode === "CA"
      ? publicRuntimeConfig.cookieDomainCa
      : publicRuntimeConfig.cookieDomain
  );
  return leaflyId!;
};

const getCartTotals = (action: LoadCartAction) => {
  let priceTotal = 0;
  let quantityTotal = 0;
  action.cart.cartItems.forEach((cartItem) => {
    quantityTotal += cartItem.quantity;
    priceTotal += cartItem.quantity * cartItem.packagePrice;
  });

  return { quantityTotal, priceTotal };
};

const reservationReducer = (
  state = initialState,
  action: AllActions
): ReservationState => {
  switch (action.type) {
    case LOAD_COOKIES:
      return {
        ...state,
        cartId: action.cookies["X-Leafly-Id"],
      };
    case RESERVATION_CREATE_CART_ID:
      return {
        ...state,
        cartId: state.cartId || createCartId(action.countryCode),
      };
    case RESERVATION_LOAD_CART:
      if (typeof document !== "undefined") {
        // Signal to cephalopod to refresh the cart
        document.dispatchEvent(new CustomEvent("getcart"));
      }

      return {
        ...state,
        cart: {
          loaded: true,
          cartItems: action.cart.cartItems,
          dispensaryId: action.cart.dispensaryId,
          dispensary: action.cart.dispensaryData,
          ...getCartTotals(action),
        },
      };
    case RESERVATION_ADD_TO_CART:
      return {
        ...state,
        status: ReservationStatus.ShowCheckoutModal,
      };
    case RESERVATION_ADD_TO_CART_ERROR:
      return {
        ...state,
        pendingMenuItem: action.pendingMenuItem,
        status: getAddToCartStatus(action),
      };
    case RESERVATION_CLOSE_MODAL:
      return {
        ...state,
        status: ReservationStatus.None,
      };
  }

  return state;
};

export default reservationReducer;
